import { push as updatePath } from 'react-router-redux';
import { defineMessages } from 'react-intl';
import { createFormAction } from 'redux-form-saga';
import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { change, initialize, SubmissionError } from 'redux-form';
import { call, put, select, spawn, takeEvery } from 'redux-saga/effects';
import { getCountries, isUserVerified } from 'redux/saga/entities/bound';
import { selectScheduleId, setScheduleId } from 'features/InventoryDetail/InventoryDetail.reducer';
import { ec, ga } from 'helpers/multitrack';
import { augmentSubmitActionWithAdditionalData } from 'helpers/forms';
import { deepExtract } from 'helpers/deep';
import { segment } from 'helpers/segment';
import Base64Deriver from 'derivers/Base64';
import { shouldSkipPhoneVerification } from 'redux/saga/selectors/phoneVerification';
import { pathSelector } from 'redux/saga/selectors/request';
import { isLoggedIn } from 'redux/saga/selectors/user';
import { skipPhoneVerification } from 'redux/modules/features/phone-verification';
import {
  ALREADY_VERIFIED,
  CODE_ERROR,
  DELIVERY_METHOD_VOICE,
  DELIVERY_METHOD_SMS,
  DUPLICATE_NUMBER,
  DUPLICATE_NUMBER_ERROR,
  ERROR_CODE_MAPPING,
} from './PhoneVerification.constants';

const ITEM_CODE = 'Code';
const ITEM_PHONE = 'Phone';

export const messages = defineMessages({
  duplicateNumber: {
    id: 'phoneVerification.numberAlreadyAssociated',
    defaultMessage:
      'That number is already associated with an account. Please verify the number and try again or reach out to our support team to complete your subscription.',
  },
});

export const LOAD_PHONE_VERIFICATION_PAGE = 'classpass/phoneVerification:LOAD_DATA';

export const CODE_VERIFICATION_FORM_NAME = 'verify-phone-code';
export const PHONE_VERIFICATION_FORM_NAME = 'verify-phone-number';

export const loadData = (countryCode, params) => ({ type: LOAD_PHONE_VERIFICATION_PAGE, countryCode, params });
export const submitPhoneNumber = createFormAction('submitPhoneNumber');
export const submitPhoneNumberAndText = augmentSubmitActionWithAdditionalData(submitPhoneNumber, {
  deliveryMethod: DELIVERY_METHOD_SMS,
});
export const submitPhoneNumberAndCall = augmentSubmitActionWithAdditionalData(submitPhoneNumber, {
  deliveryMethod: DELIVERY_METHOD_VOICE,
});
export const retryText = augmentSubmitActionWithAdditionalData(submitPhoneNumber, {
  deliveryMethod: DELIVERY_METHOD_SMS,
  retry: true,
});
export const retryCall = augmentSubmitActionWithAdditionalData(submitPhoneNumber, {
  deliveryMethod: DELIVERY_METHOD_VOICE,
  retry: true,
});
export const verifyCode = createFormAction('verifyCode');

export function* findCountry(key, value) {
  const countries = yield select(getCountries.data);
  return countries.find(country => String(country[key]) === value);
}

export function* doGetScheduleId(params) {
  const [, encodedScheduleId] = deepExtract('splat', params, []);
  const scheduleId = Base64Deriver.decodeNumber(encodedScheduleId);
  yield put(setScheduleId(scheduleId || 0));
}

export function* doHandlePostVerification() {
  // We have to reset `isUserVerified` in case a falsy value is cached
  yield put(isUserVerified.resetAction());
  const path = yield select(pathSelector);
  yield put(updatePath(path.replace(/^\/[^/]+(\/enter-code)?/, '/purchase')));
}

export function* doLoadData(client, { countryCode, params }) {
  try {
    yield call(doGetScheduleId, params);
    const countries = yield call(fetchEntityIfNeeded, getCountries, [client]);
    const countryId = countries.find(country => country.code === countryCode).id;
    yield put(change(PHONE_VERIFICATION_FORM_NAME, 'countryId', String(countryId)));
  } catch (err) {
    yield put(skipPhoneVerification());
    yield call(doHandlePostVerification);
  }
}

export function* doVerifyCodeOperation(client, { code, countryCallingCode, phoneNumber }) {
  yield call(client.phoneVerification.checkValidationCode, {
    code,
    country_code: countryCallingCode,
    phone_number: phoneNumber,
  });
  yield call(doHandlePostVerification);
  yield call(segment.track, 'Membership Purchase', {
    action: 'Phone Verification Code Input',
  });
  yield spawn(ga, 'Membership Purchase', 'Phone Verification Code Input');
}

export function* doSubmitNumberOperation(client, { countryId, deliveryMethod, phoneNumber, retry }) {
  const country = yield call(findCountry, 'id', countryId);
  const response = yield call(client.phoneVerification.sendValidationCode, {
    country_code: country.calling_code,
    delivery_method: deliveryMethod.toLowerCase(),
    phone_number: phoneNumber,
  });
  yield call(segment.track, 'Membership Purchase', {
    action: 'Phone Verification Submit Number',
  });
  yield spawn(ga, 'Membership Purchase', 'Phone Verification Submit Number');

  // Handle if the user somehow already verified
  if (response.status === ALREADY_VERIFIED) {
    yield call(doHandlePostVerification);
    return;
  }

  if (!retry) {
    yield put(
      initialize(CODE_VERIFICATION_FORM_NAME, {
        countryCallingCode: country.calling_code,
        countryShortCode: country.short_code,
        phoneNumber,
      })
    );
    const path = yield select(pathSelector);
    yield put(updatePath(path.replace(/^(\/[^/]+)/, '$1/enter-code')));
  }
}

export function* doPhoneVerificationOperation(client, form, item, operation, { payload }) {
  let error;
  try {
    yield call(operation, client, payload);
    yield put(form.success());
  } catch (err) {
    const { httpStatus, message } = err;
    error = ERROR_CODE_MAPPING[httpStatus] || { message };
    let eventAction;
    if (httpStatus === 403) {
      eventAction = CODE_ERROR;
      // As a 403 can be returned for multiple reasons, when the
      // err.message is 'Duplicate Number' we want to display a more helpful
      // to the user rather than the ERROR_CODE_MAPPING value
      if (err.message === DUPLICATE_NUMBER) {
        error = messages.duplicateNumber;
        eventAction = DUPLICATE_NUMBER_ERROR;
      }
      const scheduleId = yield select(selectScheduleId);
      yield call(segment.track, 'Membership Purchase', {
        action: eventAction,
        label: scheduleId,
      });
      yield spawn(ga, 'Membership Purchase', eventAction, scheduleId);
    }
    if (!ERROR_CODE_MAPPING[httpStatus]) {
      yield put(form.success());
      yield call(doHandlePostVerification);
      return;
    }
    yield put(form.failure(new SubmissionError({ _error: error })));
  } finally {
    let action;
    if (item === ITEM_PHONE) {
      action = `${payload.retry ? 'retry_' : ''}${payload.deliveryMethod === DELIVERY_METHOD_SMS ? 'sms' : 'call'}`;
    }

    yield call(segment.track, `Verify Phone - Submit ${item}`, {
      ...(error && { error: error.message || error.defaultMessage }),
      action,
    });

    yield spawn(ec, `Verify Phone - Submit ${item}`, {
      ...(error && { error: error.message || error.defaultMessage }),
      action,
    });
  }
}

export function* userNeedsPhoneVerification(client) {
  const shouldVerify = !(yield select(shouldSkipPhoneVerification));

  if (!shouldVerify) {
    return false;
  }

  const isVerified = (yield select(isLoggedIn)) && (yield call(fetchEntityIfNeeded, isUserVerified, [client]));
  return !isVerified;
}

export function* watchPhoneVerificationSagas(client) {
  yield takeEvery(LOAD_PHONE_VERIFICATION_PAGE, doLoadData, client);
  yield takeEvery(
    submitPhoneNumber.REQUEST,
    doPhoneVerificationOperation,
    client,
    submitPhoneNumber,
    ITEM_PHONE,
    doSubmitNumberOperation
  );
  yield takeEvery(
    verifyCode.REQUEST,
    doPhoneVerificationOperation,
    client,
    verifyCode,
    ITEM_CODE,
    doVerifyCodeOperation
  );
}
